import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { autorun } from 'mobx';
import PropTypes from 'prop-types';

import LeadsContext from '../../../../context/LeadsContext';
import UserContext from '../../../../../context/UserContext';
import LeadHistory from '../../../../Model/LeadHistory';
import UserSessions from '../../../../../Model/UserSessions';
import Lead, { SalesStage } from '../../../../Model/Lead';
import useComponentMounted from '../../../../../hooks/useComponentMounted';

import StatsCard from './components/StatsCard';
import { CardsContainer } from './styles';
import {
  calculateWonLeadsStats,
  calculateClaimedLeadsStats,
  LAST_MONTH,
  LAST_WEEK,
  calculateOnlineStats,
} from './utils';
import texts from './texts.json';

const AgentStatsSection = ({ isCoachesListReady, coachesList }) => {
  const {
    userDoc: { id: userId },
  } = useContext(UserContext);
  const { combineLeads } = useContext(LeadsContext);

  const [claimedLeadsHistory, setClaimedLeadsHistory] = useState({ docs: [] });
  const [userOnlineSessions, setUserOnlineSessions] = useState({ docs: [] });
  const [wonLeadsForMultipleCoaches, setWonLeadsForMultipleCoaches] = useState(null);
  const [claimStats, setClaimStats] = useState({ todayClaimStats: {}, thisMonthClaimStats: {} });
  const [onlineStats, setOnlineStats] = useState({ thisWeekOnlineStats: {}, lastWeekOnlineStats: {} });
  const [winStats, setWinStats] = useState({ todayWinStats: {}, thisMonthWinStats: {} });
  const [isClaimAndOnlineStatsLoading, setIsClaimAndOnlineStatsLoading] = useState(false);
  const [isWonLeadsLoading, setIsWonLeadsLoading] = useState(true);

  const isComponentMountedRef = useComponentMounted();

  const coachIds = useMemo(() => (isCoachesListReady ? Object.keys(coachesList) : []), [
    coachesList,
    isCoachesListReady,
  ]);

  useEffect(() => {
    const getClaimedLeadsAndOnlineSessions = async () => {
      setIsClaimAndOnlineStatsLoading(true);
      const promises = [
        LeadHistory.getClaimedLeadsHistory(userId, LAST_MONTH.toDate()),
        UserSessions.getUserOnlineSessionsByUserId(userId, LAST_WEEK.toDate()),
      ];
      const [claimedHistoryCol, onlineSessionsCol] = await Promise.all(promises);

      if (isComponentMountedRef.current) {
        setClaimedLeadsHistory(claimedHistoryCol);
        setUserOnlineSessions(onlineSessionsCol);
        setIsClaimAndOnlineStatsLoading(false);
      }
    };

    if (userId) {
      getClaimedLeadsAndOnlineSessions();
    }
  }, [userId, isComponentMountedRef]);

  useEffect(() => {
    const getWonLeads = async () => {
      setIsWonLeadsLoading(true);
      const wonLeadsCols = await Lead.getAllLeadsByCoachList(
        coachIds,
        LAST_MONTH.toDate(),
        userId,
        [SalesStage.WON],
        'wonAt',
      );
      if (isComponentMountedRef.current) {
        setWonLeadsForMultipleCoaches(wonLeadsCols);
        setIsWonLeadsLoading(false);
      }
    };

    if (userId && isCoachesListReady && coachIds?.length) {
      getWonLeads();
    }
  }, [userId, isComponentMountedRef, coachIds, isCoachesListReady]);

  // calculate stats for won leads, claimed leads and online sessions and observe changes in the stats
  useEffect(() => {
    const disposer = autorun(async () => {
      const [todayClaimStats, thisMonthClaimStats] = calculateClaimedLeadsStats(claimedLeadsHistory.docs);
      const [thisWeekOnlineStats, lastWeekOnlineStats] = calculateOnlineStats(userOnlineSessions.docs);

      const wonLeadsDocs = combineLeads(wonLeadsForMultipleCoaches);
      const [todayWinStats, thisMonthWinStats] = calculateWonLeadsStats(wonLeadsDocs.docs);

      if (isComponentMountedRef.current) {
        setWinStats({ todayWinStats, thisMonthWinStats });
        setClaimStats({ todayClaimStats, thisMonthClaimStats });
        setOnlineStats({ thisWeekOnlineStats, lastWeekOnlineStats });
      }
    });

    return () => {
      disposer();
    };
  }, [
    claimedLeadsHistory.docs,
    userOnlineSessions.docs,
    combineLeads,
    wonLeadsForMultipleCoaches,
    isComponentMountedRef,
  ]);

  return (
    <CardsContainer>
      <StatsCard
        title={texts.online}
        todayStats={onlineStats.thisWeekOnlineStats}
        loading={isClaimAndOnlineStatsLoading}
      />
      <StatsCard
        title={texts.claims}
        todayStats={claimStats.todayClaimStats}
        monthlyStats={claimStats.thisMonthClaimStats}
        loading={isClaimAndOnlineStatsLoading}
      />
      <StatsCard
        title={texts.wins}
        todayStats={winStats.todayWinStats}
        monthlyStats={winStats.thisMonthWinStats}
        loading={isWonLeadsLoading}
      />
    </CardsContainer>
  );
};

AgentStatsSection.propTypes = {
  isCoachesListReady: PropTypes.bool.isRequired,
  coachesList: PropTypes.object.isRequired,
};

export default AgentStatsSection;
